import type { BgColor } from '~/types/pipeline';

/** Pixels below this alpha are treated as transparent and ignored when
 *  sampling/picking bg colors — keeps anti-aliased silhouette pixels from
 *  polluting the bg color set. */
export const BG_ALPHA_MIN = 200;

/**
 * Sample a 4-pixel band around all four edges of an ImageData, quantize colors
 * to 16-step buckets (mask 0xF0 per channel), and return the top clusters by
 * frequency. Pixels with alpha < BG_ALPHA_MIN are skipped. Returns at most
 * maxClusters results, dropping any cluster with count < 10% of the top cluster.
 */
export function detectBgColors(image: ImageData, maxClusters = 3): BgColor[] {
  const { data, width, height } = image;

  const counts = new Map<number, number>();
  let totalSampled = 0;

  const sample = (x: number, y: number) => {
    if (x < 0 || x >= width || y < 0 || y >= height) return;
    const idx = (y * width + x) * 4;
    if ((data[idx + 3] ?? 0) < BG_ALPHA_MIN) return;

    const r = (data[idx] ?? 0) & 0xf0;
    const g = (data[idx + 1] ?? 0) & 0xf0;
    const b = (data[idx + 2] ?? 0) & 0xf0;
    const key = (r << 16) | (g << 8) | b;

    counts.set(key, (counts.get(key) ?? 0) + 1);
    totalSampled += 1;
  };

  const band = 4;
  for (let x = 0; x < width; x++) {
    for (let dy = 0; dy < band; dy++) {
      sample(x, dy);
      sample(x, height - 1 - dy);
    }
  }
  for (let y = 0; y < height; y++) {
    for (let dx = 0; dx < band; dx++) {
      sample(dx, y);
      sample(width - 1 - dx, y);
    }
  }

  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const top = sorted[0];
  if (!top) return [];

  const result: BgColor[] = [];
  for (const [k, count] of sorted) {
    if (count < top[1] * 0.1) break;
    if (result.length >= maxClusters) break;
    result.push({
      rgb: [(k >> 16) & 0xff, (k >> 8) & 0xff, k & 0xff],
      weight: totalSampled > 0 ? count / totalSampled : 0,
    });
  }

  return result;
}
